import { useRef, useState } from 'react';
import { signOut } from 'next-auth/react';
import { useTheme } from '@mui/material/styles';
import { Box, ButtonBase, capitalize, ClickAwayListener, Paper, Popper, Stack, Typography, alpha } from '@mui/material';
import { DownOutlined } from '@ant-design/icons';
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import ProfileTab from './ProfileTab';
import useUser from 'hooks/useUser';

const Profile = () => {
  const theme = useTheme();
  const user = useUser();

  const primary = theme.palette.primary.main;

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => setOpen((prev) => !prev);

  const handleLogout = () => {
    signOut({ redirect: false });
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={{
          pl: 0.5,
          pr: 1.5,
          py: 0.5,
          bgcolor: open ? alpha(primary, 0.08) : theme.palette.grey[50],
          border: '1px solid',
          borderColor: open ? alpha(primary, 0.24) : theme.palette.divider,
          borderRadius: 25,
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: open ? alpha(primary, 0.1) : alpha(primary, 0.04),
            borderColor: alpha(primary, 0.2),
            boxShadow: `0 2px 8px ${alpha(primary, 0.08)}`
          },
          '&:focus-visible': { outline: `2px solid ${primary}`, outlineOffset: 2 }
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        {user && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ position: 'relative' }}>
              <Avatar
                alt={user.name}
                src={user.avatar}
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  bgcolor: primary,
                  color: '#fff'
                }}
              >
                {initials}
              </Avatar>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -1,
                  right: -1,
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  border: '1.5px solid #fff'
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight={500} color="text.primary" sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {capitalize(user.name)}
            </Typography>
            <DownOutlined
              rev={undefined}
              style={{
                fontSize: 9,
                color: theme.palette.text.secondary,
                transition: 'transform 0.2s ease',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                marginLeft: 2
              }}
            />
          </Stack>
        )}
      </ButtonBase>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 8] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            {open && (
              <Paper
                sx={{
                  boxShadow: `0 12px 40px ${alpha(theme.palette.text.primary, 0.1)}, 0 2px 8px ${alpha(theme.palette.text.primary, 0.04)}`,
                  border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                  borderRadius: 3,
                  width: 280,
                  overflow: 'hidden'
                }}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <MainCard elevation={0} border={false} content={false}>
                    <Box
                      sx={{
                        px: 2.5,
                        pt: 2.5,
                        pb: 2,
                        background: `linear-gradient(135deg, ${alpha(primary, 0.06)} 0%, ${alpha(primary, 0.02)} 100%)`
                      }}
                    >
                      {user && (
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box sx={{ position: 'relative' }}>
                            <Avatar
                              alt={user.name}
                              src={user.avatar}
                              sx={{
                                width: 44,
                                height: 44,
                                fontSize: '1rem',
                                fontWeight: 600,
                                bgcolor: primary,
                                color: '#fff',
                                border: `2px solid ${alpha(primary, 0.2)}`
                              }}
                            >
                              {initials}
                            </Avatar>
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 1,
                                right: 1,
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: theme.palette.success.main,
                                border: '2px solid #fff'
                              }}
                            />
                          </Box>
                          <Stack spacing={0.15} sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600} noWrap>
                              {capitalize(user.name)}
                            </Typography>
                            {user.email && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {user.email}
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      )}
                    </Box>

                    <ProfileTab handleLogout={handleLogout} />
                  </MainCard>
                </ClickAwayListener>
              </Paper>
            )}
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Profile;
